import { type AutonomyFunctionDTO } from '@/domain/autonomylevels/types';
import { type Benefit } from '@/domain/benefits/types';
import { type Country } from '@/domain/country/types';
import { type Industry } from '@/domain/industry/types';
import { type LifeCycle } from '@/domain/lifeCycles/types';
import { type MaturityModel } from '@/domain/maturityModel/types';
import { type News } from '@/domain/news/types';
import { type OKR } from '@/domain/okrs/type';
import { type UpdateRelatedPlantDTO } from '@/domain/plant/types';
import { type ProductLine } from '@/domain/productLine/types';
import { type ProductPortfolio } from '@/domain/productPortfolio/types';
import { type SourceCode } from '@/domain/productSourceCode/types';
import { type QualityAssuranceProcedure } from '@/domain/qualityAssurance/types';
import {
  type PlatformVersionDTO,
  type RoadMap,
  type RoadmapPlatform,
  type RoadmapStatus,
} from '@/domain/roadmap/types';
import { type RoadMapType } from '@/domain/roadmapType/types';
import { type Training } from '@/domain/trainings/types';
import { type BasicUnit } from '@/domain/units/types';
import { type UserBrief } from '@/domain/user/types';
import { type UserPersona } from '@/domain/userPersonas/types';

export interface ProductStatus {
  idProductStatus: number;
  productStatus: string;
}

export interface ProductList {
  idProduct: number;
  product: string;
  nickName: string;
  capabilitySecurityLevel: number | null;
  productUnit: BasicUnit;
  description: string;
  externalAccess: true;
  enabled: true;
  group: Group;
  platform?: RoadmapPlatform;
  productOwner: UserBrief | null;
  technologyManager: UserBrief | null;
  productManager: UserBrief | null;
  cyberSecurityExpert: UserBrief | null;
  qaSpecialistsIds: number[];
  idProductOwner: number;
  idTechnologyManager: number;
  idProductManager: number;
  idType: number;
  saleable: boolean;
  isRelease: boolean;
  approval: ProductApproval;
  cyberSecurityExpertId: number;
  productUnitId: number | null;
  hasDraft?: boolean;
  createdAt: string;
}

export interface ProductApproval {
  poApproved: boolean;
  pmApproved: boolean;
}

export interface ProductDraft {
  idDraft: number;
  idProduct: number | null;
  payload: ProductUpdate;
  aiReviewNote?: string | null;
  updatedAt: string;
  rowVersion: string;
}

export interface ImportAIZipProductDraftResponse {
  importToken: string;
  totalFiles: number;
  validCount: number;
  invalidCount: number;
  products: ImportAIProductDraft[];
  conflictsCount: number;
}

export interface ImportAIProductDraft {
  fileName: string;
  productData: object;
  isValid: boolean;
  errors: string[];
  conflicts: string[];
}

export interface ImportAIProductData {
  id: number;
  shortDescription: string;
  fullDescription: string;
  features: ImportAIProductFeatures[];
  kpis: ImportAIProductKPI[];
  faq: ImportAIProductFAQ[];
  metaTitle: string;
  metaDescription: string;
  keywords: string;
  editorNotes: string;
}

export interface ImportAIProductFeatures {
  title: string;
  description: string;
}

export interface ImportAIProductKPI {
  statement: string;
}

export interface ImportAIProductFAQ {
  question: string;
  answer: string;
}

export interface ProductFaq {
  id: number;
  productId: number;
  question: string;
  answer: string;
  order: number;
}

export interface ProductKpi {
  id: number;
  productId: number;
  statement: string;
  order: number;
}

export interface ProductFeature {
  id: number;
  productId: number;
  title: string;
  description: string;
  order: number;
}

export interface SaveProductDraftDTO {
  idProduct?: number | null;
  payload: ProductUpdate;
  aiReviewNote?: string;
  rowVersion?: string | null;
}
export interface ProductSimple extends ProductList {
  image: string;
  image2: string;
  status?: ProductStatus;
  industries: Industry[];
  lifeCycles: LifeCycle[];
  benefits: Benefit[];
  userPersonas: UserPersona[];
  countries: Country[];
  portfolios: Array<Omit<ProductPortfolio, 'sections, description'>>;
  productOwner: UserBrief | null;
  documentation: string;
  archived: boolean;
}

export interface ProductDocumentLinkDTO {
  idProductDocumentLink: number;
  idProductDocumentType: number;
  documentName: string;
  documentLink: string;
  documentType?: ProductDocumentTypeDTO;
}
export interface Product {
  idProduct: number;
  product: string;
  nickName: string;
  capabilitySecurityLevel: number | null;
  productUnitId: number | null;
  description: string;
  descriptionForProposals?: string;
  businessModels: string;
  image: string;
  image2: string;
  documentation: string;
  enabled: boolean;
  externalAccess: boolean;
  archived: boolean;
  linkYammer?: string;
  downloadLink?: string;
  status: ProductStatus;
  group: Group;
  platform: RoadmapPlatform | null;
  productOwner: UserBrief | null;
  productManager: UserBrief | null;
  technologyManager: UserBrief | null;
  cyberSecurityExpert: UserBrief | null;
  cyberSecurityExpertId: number | null;
  countries: Country[];
  videos: ProductVideo[];
  streamVideos: StreamVideo[];
  news: News[];
  industries: RelatedIndustry[];
  benefits: Benefit[];
  lifeCycles: LifeCycle[];
  includedProducts: ProductBrief[];
  relatedProducts: ProductBrief[];
  relatedAddons: ProductBrief[];
  trainings: Training[];
  userPersonas: UserPersona[];
  roadmaps: RoadMap[] | null;
  relatedPlants: UpdateRelatedPlantDTO[];
  qaSpecialists: UserBrief[];
  procedures: QualityAssuranceProcedure[];
  documents: ProductDocumentLinkDTO[] | null;
  sourceCodes: SourceCode[] | null;
  autonomyFunctions: EditableProductAutonomyFunction[];
  idEam?: number;
  idType: number;
  saleable: boolean;
  idMaturityModel: number | null;
  approval?: ProductApproval;
  poApproved?: boolean;
  pmApproved?: boolean;
  createdAt: string;
  productFaq: ProductFaq[];
  productKpis: ProductKpi[];
  productFeatures: ProductFeature[];
}

export type ProductBrief = {
  idProduct: number;
  product: string;
  nickName: string;
  capabilitySecurityLevel: number | null;
  productUnitId: number | null;
  description: string;
  image: string;
  image2: string;
  productGroup?: string;
  productOwner?: UserBrief | null;
  platform: RoadmapPlatform | null;
  idPlatform: number | null;
  idProductOwner: number;
  idTechnologyManager: number;
  cyberSecurityExpertId: number;
  idProductManager: number;
  qaSpecialistsIds?: number[];
  industries?: Industry[];
  idType: number;
  status?: ProductStatus;
  documentation: string;
};

export type ProductBasic = {
  idProduct: number;
  product: string;
};

export type ProductSitemap = {
  idProduct: number;
  product: string;
  status: ProductStatus;
  archived: boolean;
  enabled: boolean;
  updatedAt: Date;
  group: { productGroup: string };
  industries: { industry: string }[];
};

export type ProductImportResult = {
  success: boolean;
  failedProducts: ImportProductApiError[];
};

export interface ProductRoadmap {
  idRoadmap: number;
  item: string;
  type: RoadMapType | null;
  idOwner?: number;
  version?: PlatformVersionDTO;
  status: RoadmapStatus;
  percentComplete: number;
  okrs: OKR[];
  locked: boolean;
}

export interface ProductRoadmapWithProduct extends ProductRoadmap {
  product: Product | ProductBrief;
}

export interface ProductDocumentTypeDTO {
  idProductDocumentType: number;
  name: string;
}

export interface ProductMaturityHistory {
  idMaturityModelGroupItemsHistory: number;
  idItem: number;
  item: string;
  idOption: number;
  option: string;
  optionValue: number;
  user: string;
  editedTimestamp: string;
}

export interface ProductMaturityHistoryGroup {
  group: string;
  groupOrder: number;
  items: ProductMaturityHistory[];
}

export type ProductUpdate = {
  idProduct: number;
  product: string;
  nickName: string;
  capabilitySecurityLevel: number | null;
  productUnitId: number | null;
  description: string;
  descriptionForProposals?: string;
  businessModels: string;
  image: string;
  image2: string;
  documentation: string;
  linkYammer: string;
  downloadLink: string;
  idProductStatus: number;
  idProductGroup: number;
  idProductOwner?: number;
  idMaturityModel?: number;
  idTechnologyManager?: number;
  idProductManager?: number;
  idPlatform?: number;
  idEam?: number;
  cyberSecurityExpertId?: number | null;
  saleable?: boolean;
  idType?: number;
  enabled: boolean;
  externalAccess: boolean;
  industries: RelatedIndustry[];
  lifeCycles: number[];
  benefits: BenefitAdd[];
  relatedProducts: number[];
  relatedAddons: number[];
  relatedPlants: UpdateRelatedPlantDTO[];
  trainings: Training[];
  userPersonas: number[];
  includedProducts: number[];
  news: NewsUpdate[];
  qaSpecialists: number[];
  videos: ProductVideo[];
  streamVideos: StreamVideo[];
  maturityModel?: MaturityModel | null;
  documents: ProductDocumentLinkDTO[];
  sourceCodes: SourceCode[];
  autonomyFunctions: EditableProductAutonomyFunction[];
  productFaq?: ProductFaq[];
  productKpis?: ProductKpi[];
  productFeatures?: ProductFeature[];
  pmApproved?: boolean;
  poApproved?: boolean;
  aiReviewNote?: string;
};

export interface ExportableSolution {
  productId: number;
  productName: string;
  productFamily: string;
  productFamilyId: number;
  technicalProductOwner: string;
  technicalProductOwnerEmail: string;
}

export interface RelatedIndustry extends Industry {
  idProductIndustry: number;
  idIndustryArea?: number | null;
}

export interface ImpactOption {
  id: number;
  impact: string;
}

export const impactOptions = [
  {
    id: 0,
    impact: 'Low',
  },
  {
    id: 1,
    impact: 'Medium',
  },
  {
    id: 2,
    impact: 'High',
  },
];

export const securityLevelOptions = [
  {
    value: 0,
    label: 'Level 0',
    tooltip: 'Security Level 0 where no special requirement or protection is required.',
  },
  {
    value: 1,
    label: 'Level 1',
    tooltip: 'Security Level 1 is where protection against unintentional or accidental misuse is enabled.',
  },
  {
    value: 2,
    label: 'Level 2',
    tooltip:
      'Security Level 2 is where protection against intentional misuse by simple means with few resources, general skills, and low motivation.',
  },
  {
    value: 3,
    label: 'Level 3',
    tooltip:
      'Security Level 3 is where protection against intentional misuse by sophisticated means with moderate resources, IACS-specific knowledge, and reasonable motivation is permitted.',
  },
  {
    value: 4,
    label: 'Level 4',
    tooltip:
      'Security Level 4 is where protection against intentional misuse using sophisticated means with extensive resources, IACS- specific knowledge, and high motivation is allowed.',
  },
];

export enum Industries {
  ALL_SOLUTIONS = 'All Solutions',
  GENERIC = 'Generic',
  SPECIFIC = 'Specific',
}

export interface BusinessModel {
  idBusinessModel: number;
  businessModel: string;
}

export interface Group {
  idProductGroup: number;
  productGroup: string;
  productLine: ProductLine | null;
}

export type Status = 'Released' | 'Preview' | 'Development' | 'Productize';

export type ProductVideo = {
  idProductVideo: number;
  idProduct: number;
  title: string;
  hyperlink: string;
  timestamp: string | null;
};

export type StreamVideo = {
  idProductStream: number;
  idProduct: number;
  title: string;
  description: string;
  hyperlink: string;
  timestamp: string | null;
};

export interface EditableProductAutonomyFunction extends ProductAutonomyFunction {
  edited: boolean;
}

export type BenefitAdd = {
  idBenefit: number;
  impact: {
    impact: string;
    id: number;
  };
};

export type OKRAdd = {
  id: number;
  impact: {
    impact: string;
    id: number;
  };
};

export type UnitAdd = {
  unitId: number;
  isRequested?: boolean;
};

export interface NewsUpdate extends News {
  edited: boolean;
}

export type SolutionTypes = 'Product' | 'Component';

export interface SolutionType {
  value: number;
  name: SolutionTypes;
}

export interface EditableProduct
  extends Omit<
    Product,
    'benefits' | 'industries' | 'news' | 'autonomyLevel' | 'status' | 'type' | 'group' | 'procedures' | 'sourceCodes'
  > {
  maturityModel?: MaturityModel | null;
  status?: ProductStatus;
  group?: Group;
  benefits: BenefitAdd[];
  industries: RelatedIndustry[];
  news: NewsUpdate[];
  sourceCodes: SourceCode[];
  createdAt: string;
  aiReviewNote?: string;
}

export interface PlantRelatedProduct {
  idProductCustomer: number;
  idProduct: number;
  product: string;
  details: string;
  status: number | null;
  endDate: string | null;
  startDate: string | null;
  confidential: boolean;
  quantity: number;
}

export type RelatedProduct = {
  idProduct: number;
  keyName: string;
  product: string;
  nickName: string;
  capabilitySecurityLevel: number | null;
  productUnitId: number | null;
  description: string;
  image: string;
  image2: string;
};

export interface ProductAutonomyFunction extends AutonomyFunctionDTO {
  idProductAutonomyFunction: number;
  impact: number;
}

export interface ImportError {
  line: number;
  messages: string[];
  rawRow?: Record<string, unknown>;
}

export interface ImportProductApiError {
  productName: string;
  errorMessage: string;
}

export interface ImportResult {
  payload: ProductUpdate[];
  errors: ImportError[];
}

export interface GetProductsSimpleQuery {
  showProducts?: boolean;
  showAddons?: boolean;
  industry?: string;
  productGroup?: string;
}
